import React from "react";
import styled from "styled-components";
import { YN_TYPES, YN_LABEL, parseSpecifyWhen, optionHasSpecify, noPath } from "./data";

// Read-only rendering of everything a question carries besides its text: the Specify
// follow-up and, for a checklist, its select mode and per-option settings (free-text
// follow-up, helper text, and which questions checking it reveals).
//
// Authoring shows all of this in inputs; without it the read-only views — the frozen
// Questions tab, version history, the template/version picker — reduced a checklist to
// a bare list of labels, so a reviewer could not tell single from multi select, nor see
// that an option branches to another question.
//
// Shared by QuestionRowsEditor (row shape: questionType/questionText) and
// VersionContent.QuestionsView (renderer shape: type/text). Both carry the same option
// shape, which is all this reads.

const Line = styled.div`
  font-size: 11px;
  color: #6c757d;
  margin-top: 2px;
`;

const OptList = styled.div`
  margin-top: 4px;
`;

const OptRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11px;
  color: #6c757d;
  padding-left: 6px;
  line-height: 1.7;
`;

const Badge = styled.span`
  display: inline-block;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$solo ? "#ffd8a8" : "#b2d8f7")};
  background: ${(p) => (p.$solo ? "#fff4e6" : "#e7f5ff")};
  color: ${(p) => (p.$solo ? "#b56a00" : "#0b6bcb")};
  white-space: nowrap;
`;

// 옵션에 딸린 설정 하나를 담는 꼬리표. 변경 내역 화면(VersionDiff)도 같은 것을 써서,
// 리뷰어가 Questions 탭에서 보던 것과 같은 모양·같은 말로 읽게 한다.
export const OptionTag = styled.span`
  font-size: 10.5px;
  padding: 1px 6px;
  border-radius: 3px;
  background: ${(p) => (p.$shows ? "#f3f0ff" : "#f8f9fa")};
  color: ${(p) => (p.$shows ? "#5f3dc4" : "#868e96")};
  border: 1px solid ${(p) => (p.$shows ? "#d0bfff" : "#e9ecef")};
`;

const Faint = styled.span`
  color: #adb5bd;
  font-style: italic;
`;

// "2-3. Do you have a PI?" — 섹션 번호, 질문 번호, 질문 문구 순. 옵션이 여는 질문은
// 다른 섹션에 있을 수도 있어서 질문 번호만으로는 어디를 가리키는지 알 수 없다.
// questionKey는 내부 식별자라 여기서는 쓰지 않는다 — 리뷰어가 읽는 이름은 문구다.
//
// 하위 문항은 부모마다 1번부터 다시 매겨지므로 번호만으로는 서로 구별되지 않는다.
// byKey(questionKey -> 문항)를 넘기면 data.noPath가 부모 번호를 앞에 붙여
// "2-3-1."까지 만들어 준다. 넘기지 않으면 예전처럼 "2-1."에서 멈춘다 — 계층을 쓰지
// 않는 호출자는 그대로 두어도 되게.
export function questionLabel(q, byKey) {
  if (!q) return null;
  const sec  = q.sectionNo ?? q.section;
  const text = q.text ?? q.questionText;
  const num  = [sec, noPath(q, byKey)].filter((v) => v != null && v !== "").join("-");
  return `${num ? `${num}. ` : ""}${text || "(내용 없음)"}`;
}

// The Specify follow-up of a Y/N(/NA) question, with the answers that trigger it.
export function SpecifyDetail({ type, hasSpecify, specifyLabel, specifyWhen }) {
  if (!hasSpecify || !YN_TYPES.includes(type)) return null;
  const when = parseSpecifyWhen(specifyWhen);
  return (
    <Line>
      ↳ {specifyLabel || "Specify:"}{" "}
      <Faint>
        ({when.length ? `${when.map((c) => YN_LABEL[c] || c).join("/")}일 때` : "항상"} 표시)
      </Faint>
    </Line>
  );
}

// A checklist's options. `resolveQuestion(key)` turns a showsQuestions key into a
// display label. A key it can't resolve points at a question that is no longer in this
// set — deleted, deactivated, or never pinned into this version — and is named as such
// rather than shown raw: the key is an internal identifier, not something a reviewer
// reads. The editable table still shows the keys, where they are what gets clicked.
export function ChecklistDetail({ options, singleSelect, resolveQuestion }) {
  const opts = options || [];
  return (
    <OptList>
      <Badge
        $solo={!!singleSelect}
        title={singleSelect
          ? "옵션 중 하나만 선택할 수 있습니다."
          : "옵션을 여러 개 선택할 수 있습니다."}
      >
        {singleSelect ? "단일 선택 (하나만)" : "다중 선택 (여러 개)"}
      </Badge>
      {!opts.length && <Line><Faint>옵션이 없습니다.</Faint></Line>}
      {opts.map((opt, i) => {
        const shows = (opt.showsQuestions || []).map(
          (key) => (resolveQuestion && resolveQuestion(key)) || "(없는 질문)"
        );
        return (
          <OptRow key={opt.key || opt._uid || i}>
            <span>
              ☐ {opt.label || <Faint>(라벨 없음)</Faint>}
            </span>
            {optionHasSpecify(opt) && (
              <OptionTag title="이 옵션을 선택하면 자유 입력란이 나타납니다.">
                입력란{opt.specifyLabel ? `: ${opt.specifyLabel}` : ""}
              </OptionTag>
            )}
            {opt.helperText && <OptionTag title="옵션 아래에 항상 표시되는 안내문">안내: {opt.helperText}</OptionTag>}
            {shows.length > 0 && (
              <OptionTag $shows title="이 옵션을 선택하면 아래 질문이 나타납니다.">
                선택 시 표시 → {shows.join(", ")}
              </OptionTag>
            )}
          </OptRow>
        );
      })}
    </OptList>
  );
}
